import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as PdfIcon } from '../../../assets/icons/pdf.svg';
import Button from '../../../composants/Button/Button';
import ValidationPopUp from '../../../composants/ValidationPopUp/ValidationPopUp';
import { PrintButton } from '../../../composants/PrintButton/PrintButton';
import NextTabButton from '../../../composants/NextTabButton/NextTabButton';
import MailPreview from '../../../composants/MailPreview/MailPreview';
import Banner from '../../../composants/Banner/Banner';
import SignatureFlow from '../../../composants/Marche/SignatureFlow';
import { useAuth } from '../../../contexts/AuthContext';
import { StatusId, OperationMS } from '../types';
import { CommandeAnalyse } from '../domain/analyse/types';
import { statusColorById, canAnnulerOperation } from '../domain/operationStatus';
import {
  getRetenuCommande,
  getNonRetenusCommandes,
} from '../domain/marche/marcheAttribution';
import { getDemandesSignature, getNotificationDate, getSignatureAcheteurInfo } from '../domain/analyse/historique';
import { formatDateHeure } from '../domain/analyse/dates';
import {
  isAcheteurSimple,
  isAcheteurSignataire,
  isDesignatedAcheteurSignataire,
} from '../domain/marche/marcheRoles';
import { buildMailPreview } from '../domain/marche/mailTemplate';
import { notificationsNextTab } from '../domain/nextTab';
import {
  getSignatureMode,
  readCachedSignatureMode,
  cacheSignatureMode,
  getMarcheSignataireInfo,
  getSignedNames,
  requestSignature,
  signPublic,
  fetchSignedContratBlob,
  triggerBlobDownload,
} from '../services/operationMarcheService';
import { getSignataires, annulerOperation, SignataireApi } from '../services/operationMsService';
import SectionPliable from '../../../composants/SectionPliable/SectionPliable';
import OperationMarchePreparation from './OperationMarchePreparation';
import OperationMarcheCandidats from './OperationMarcheCandidats';
import DocumentsList from '../../../composants/DocumentsList/DocumentsList';
import { useMarcheDocuments } from '../hooks/useMarcheDocuments';
import ModalProfilMembre from '../../Publications/components/DetailCandidature/ModalProfilMembre';
import type { ProfileData } from '../../../services/profileService';

// Onglet "Notifications et signatures" (Marché) — ACHETEUR. Fork acheteur-seul du legacy Marche
// (bi-role) : drive sur `operation`, branches presta supprimees. Les DEUX modes de signature sont
// preserves (manuscrite via SignatureFlow + OTP ; automatique via signPublic). SignatureFlow est
// reutilise avec le fix OTP (nom visible : hideNameEdit omis -> false, requireOtp explicite).
interface OperationMarcheProps {
  operation: OperationMS;
  refetch: () => void;
  printRef: React.RefObject<HTMLElement>;
}

const stripP = (value?: string | number): string => {
  const s = String(value ?? '');
  return s.startsWith('P') ? s.substring(1) : s;
};

const OperationMarche: React.FC<OperationMarcheProps> = ({ operation, refetch, printRef }) => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [signatureMode, setSignatureMode] = useState(readCachedSignatureMode());
  const [msPdfBlobUrl, setMsPdfBlobUrl] = useState<string | null>(null);
  const [loadingMsPdf, setLoadingMsPdf] = useState(false);
  const [nomSignataireMs, setNomSignataireMs] = useState('');
  const [signedNomAcheteur, setSignedNomAcheteur] = useState<string | undefined>(undefined);
  const [signedNomPrestataire, setSignedNomPrestataire] = useState<string | undefined>(undefined);
  const [acheteurSignataires, setAcheteurSignataires] = useState<SignataireApi[]>([]);
  const [selectedSignataireId, setSelectedSignataireId] = useState<number | null>(null);
  const [loadingSignataires, setLoadingSignataires] = useState(false);
  const [sendingRequest, setSendingRequest] = useState(false);
  const [signing, setSigning] = useState(false);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [autoSignSuccess, setAutoSignSuccess] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [selectedCommandeForPreview, setSelectedCommandeForPreview] = useState<CommandeAnalyse | null>(null);
  const [isAnnulerOpen, setIsAnnulerOpen] = useState(false);
  const [isAnnuling, setIsAnnuling] = useState(false);
  const [editingSignataire, setEditingSignataire] = useState(false);
  const [errorModal, setErrorModal] = useState<string | null>(null);

  const operationStatus = operation.status?.id;
  const retenu = getRetenuCommande(operation);
  const nonRetenus = getNonRetenusCommandes(operation);
  // Documents associes au marche (Contrat + 2 CCTP), memes endpoints que Coordonnees.
  const { documents: marcheDocuments, loading: marcheDocsLoading } = useMarcheDocuments(retenu);

  // Popup profil du titulaire retenu (reutilise la modale annuaire, ouverte sur l'onglet Documents).
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const titulaireInterlocuteur = retenu?.contrat?.entite?.interlocuteur ?? null;
  const profileContact = titulaireInterlocuteur
    ? {
        nom: titulaireInterlocuteur.nom || '',
        prenom: titulaireInterlocuteur.prenom || '',
        email: titulaireInterlocuteur.email_contact || '',
        telephone: titulaireInterlocuteur.telephone || '',
        fonction: titulaireInterlocuteur.fonction || '',
        contratNumero: retenu?.contrat?.numero ?? null,
      }
    : null;
  const openTitulaireProfil = useCallback(() => {
    const entiteId = retenu?.contrat?.entite?.id;
    if (entiteId === undefined) return;
    setProfile(null);
    setProfileError(null);
    setProfileLoading(true);
    setShowProfileModal(true);
    fetch(`${process.env.REACT_APP_API_URL}/annuaire/entites/${entiteId}/profil`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
    })
      .then((r) => {
        if (!r.ok) throw new Error(`Erreur ${r.status}`);
        return r.json();
      })
      .then((d: { data?: ProfileData } & Partial<ProfileData>) => setProfile((d?.data ?? d) as ProfileData))
      .catch((e: Error) => setProfileError(e.message || 'Impossible de charger le profil.'))
      .finally(() => setProfileLoading(false));
  }, [retenu?.contrat?.entite?.id]);

  // Date d'envoi optimiste des notifications aux non-retenus : commandeId -> ISO. Affichee
  // immediatement apres l'envoi ; le refetch (getNotificationDate, date backend) prend ensuite
  // le relais (fallback uniquement, donc la date backend est prioritaire une fois revenue).
  const [justNotified, setJustNotified] = useState<Record<string, string>>({});
  const handleCandidatNotified = useCallback(
    (commandeId: number) => {
      setJustNotified((prev) => ({ ...prev, [String(commandeId)]: new Date().toISOString() }));
      refetch();
    },
    [refetch],
  );

  // Mode de signature (API autorite, cache local en secours).
  useEffect(() => {
    getSignatureMode()
      .then((mode) => {
        if (mode) {
          cacheSignatureMode(mode);
          setSignatureMode(mode);
        }
      })
      .catch(() => undefined);
  }, []);

  // PDF du MS pour la previsualisation (statut 13).
  useEffect(() => {
    const numero = operation.numero;
    if (!numero) return undefined;
    let revoked = false;
    let url: string | null = null;
    setLoadingMsPdf(true);
    fetchSignedContratBlob(stripP(numero))
      .then((blob) => {
        if (revoked) return;
        url = URL.createObjectURL(blob);
        setMsPdfBlobUrl(url);
      })
      .catch(() => setMsPdfBlobUrl(null))
      .finally(() => setLoadingMsPdf(false));
    return () => {
      revoked = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [operation.numero]);

  // Nom du signataire acheteur (prenom + nom reels).
  useEffect(() => {
    if (!operation.id) return undefined;
    let active = true;
    getMarcheSignataireInfo(operation.id)
      .then((data) => {
        if (!active || !data.signataire) return;
        const full = `${data.signataire.prenom ?? ''} ${data.signataire.nom ?? ''}`.trim();
        if (full) setNomSignataireMs(full);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [operation.id]);

  // Noms reellement utilises a la signature (pour l'affichage Coordonnees).
  useEffect(() => {
    if (!operation.id) return undefined;
    let active = true;
    getSignedNames(operation.id, operation.id_commande ?? retenu?.id)
      .then((data) => {
        if (!active) return;
        if (data.acheteur?.signataire_nom) setSignedNomAcheteur(data.acheteur.signataire_nom);
        if (data.prestataire?.signataire_nom) setSignedNomPrestataire(data.prestataire.signataire_nom);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [operation.id, operation.id_commande, retenu?.id]);

  // Signataires acheteur de l'organisme (pour la demande par mail).
  useEffect(() => {
    const organismeId = operation.organisme?.id;
    if (!organismeId) return undefined;
    let active = true;
    setLoadingSignataires(true);
    getSignataires(organismeId)
      .then((res) => {
        if (active) setAcheteurSignataires(res.data ?? []);
      })
      .catch(() => {
        if (active) setAcheteurSignataires([]);
      })
      .finally(() => {
        if (active) setLoadingSignataires(false);
      });
    return () => {
      active = false;
    };
  }, [operation.organisme?.id]);

  const signRetenu = useCallback(async () => {
    if (retenu?.id === undefined) {
      setErrorModal('Impossible de trouver la commande retenue pour la signature.');
      return;
    }
    setSigning(true);
    try {
      await signPublic({ operation_id: operation.id, commande_id: retenu.id });
      setAutoSignSuccess(true);
    } catch (e) {
      setErrorModal(e instanceof Error ? e.message : 'Impossible de signer le marché.');
    } finally {
      setSigning(false);
    }
  }, [operation.id, retenu?.id]);

  // Mode manuscrite -> modale SignatureFlow (OTP + dessin) ; mode automatique -> signature serveur.
  const handleSendToPrestataire = useCallback(() => {
    if (signatureMode === 'manuscrite') {
      setShowSignatureModal(true);
      return;
    }
    signRetenu();
  }, [signatureMode, signRetenu]);

  const handleRequestSignature = useCallback(async () => {
    if (!selectedSignataireId) return;
    const selected = acheteurSignataires.find((s) => s.id === selectedSignataireId);
    setSendingRequest(true);
    try {
      await requestSignature({
        operation_id: operation.id,
        signataire_id: selectedSignataireId,
        signataire_type: selected?.type_signataire,
      });
      refetch();
    } catch (e) {
      setErrorModal(e instanceof Error ? e.message : "Impossible d'envoyer la demande de signature.");
    } finally {
      setSendingRequest(false);
    }
  }, [selectedSignataireId, acheteurSignataires, operation.id, refetch]);

  const handleDownloadSigned = useCallback(async () => {
    try {
      const blob = await fetchSignedContratBlob(operation.numero ?? operation.id);
      const annee = process.env.REACT_APP_ANNEE_EDITION || '25';
      triggerBlobDownload(blob, `${annee}${operation.numero ?? ''} (signed).pdf`);
    } catch {
      setErrorModal('Une erreur est survenue lors du téléchargement du marché signé.');
    }
  }, [operation.numero, operation.id]);

  // Date d'envoi de la notification (null si pas encore envoyée) : retenu = date de signature
  // du marché (notif auto, statut Validé) ; non-retenu = date de l'évènement Notification (ou
  // optimiste juste après l'envoi). Source unique consommée par l'aperçu, l'impression et le titre.
  const computeDateEnvoi = useCallback(
    (commande: CommandeAnalyse): string | null => {
      if (commande.id === operation.id_commande) {
        return operation.status?.id === StatusId.Valider
          ? formatDateHeure(getSignatureAcheteurInfo(operation.historique).date)
          : null;
      }
      return formatDateHeure(
        getNotificationDate(operation.historique, commande.id) ?? justNotified[String(commande.id)] ?? '',
      );
    },
    [operation, justNotified],
  );

  // Aperçu mail -> impression (HTML a styles inline, plus de fetch de CSS externe).
  const handlePrintMail = useCallback(
    (commande: CommandeAnalyse) => {
      const html = buildMailPreview(commande, operation, computeDateEnvoi(commande));
      const iframe = document.createElement('iframe');
      // sandbox sans allow-scripts : l'impression marche, mais aucun script du HTML ne s'execute
      // (defense en profondeur en plus de l'echappement dans buildMailPreview).
      iframe.setAttribute('sandbox', 'allow-same-origin allow-modals');
      iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
      document.body.appendChild(iframe);
      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (!doc) return;
      doc.open();
      doc.write(html);
      doc.close();
      iframe.onload = () => {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
        window.setTimeout(() => document.body.removeChild(iframe), 100);
      };
    },
    [operation, computeDateEnvoi],
  );

  const handleAnnuler = useCallback(async () => {
    setIsAnnuling(true);
    try {
      await annulerOperation(operation.id);
      navigate('/operations');
    } catch (e) {
      setErrorModal(e instanceof Error ? e.message : "Erreur lors de l'annulation de l'opération.");
    } finally {
      setIsAnnuling(false);
      setIsAnnulerOpen(false);
    }
  }, [operation.id, navigate]);

  const openPreview = (commande: CommandeAnalyse): void => {
    setSelectedCommandeForPreview(commande);
    setIsPreviewOpen(true);
  };

  const generateMail = useCallback(
    (commande: CommandeAnalyse): string => buildMailPreview(commande, operation, computeDateEnvoi(commande)),
    [operation, computeDateEnvoi],
  );

  const isUserDesignated = isDesignatedAcheteurSignataire(operation, user?.id);

  // Banniere de statut (refonte) : vert si signe (11), orange en attente de signature acheteur
  // (13 manuscrite / 7 auto), gris standstill toujours affiche (texte indicatif).
  const sigInfo = getSignatureAcheteurInfo(operation.historique);
  const signataireNom = signedNomAcheteur || nomSignataireMs || sigInfo.operateur || '';
  const marcheSigne = operationStatus === StatusId.Valider;
  const enAttenteSignature =
    operationStatus === StatusId.ANotifier || operationStatus === StatusId.ASigner;

  // Statut signable acheteur (manuscrite 13 + role signataire). Le bouton « Signer et notifier »
  // est reutilise en 3 endroits (haut de section, bloc signataire, barre du bas) ; verrouille
  // pendant l'edition du nom du signataire.
  const canSign = operationStatus === StatusId.ANotifier && isAcheteurSignataire(user?.role);
  // « Signer et notifier » est le CTA de l'etape quand il est la : le renvoi vers la messagerie
  // s'efface derriere lui. Le telechargement du marche signe, lui, cohabite : il ne fait pas
  // avancer le workflow.
  const notificationsCta = notificationsNextTab(operation, canSign);
  const signerButton = (
    <Button
      variant="primary"
      value={signing ? 'Signature et envoi en cours...' : 'Signer et notifier'}
      onClick={handleSendToPrestataire}
      disabled={signing || editingSignataire}
    />
  );

  // Statut 11 (signé) : bouton de telechargement du marche signe, aux memes emplacements que le
  // bouton de signature (haut de section + barre du bas), a la place du bouton « Signer ».
  const downloadButton = (
    <Button
      value="Télécharger le marché subséquent signé"
      variant="secondary"
      icon={<PdfIcon />}
      onClick={handleDownloadSigned}
    />
  );

  // Bannieres d'action/etat (vert signe, orange en attente) : rappelees en haut ET en bas de
  // l'onglet, juste avant les boutons. Les blocs d'info gris restent en haut uniquement.
  const stateBanners = (
    <>
      {marcheSigne && (
        <Banner
          variant="success"
          filled
          message={`Le marché subséquent N°${operation.numero ?? ''} a été notifié au titulaire${
            sigInfo.date ? ` le ${formatDateHeure(sigInfo.date)}` : ''
          }${signataireNom ? ` par ${signataireNom}` : ''}. Les onglets de messagerie opérationnelle et d'évaluation du prestataire sont désormais actifs.`}
        />
      )}
      {enAttenteSignature && (
        <Banner
          variant="warning"
          filled
          message={"Le marché subséquent est en attente de signature de la part de l'acheteur. Dès que vous cliquerez sur « Signer le marché », la notification au titulaire sera automatiquement effectuée."}
        />
      )}
    </>
  );

  return (
    <div className="opms__marche">
      {stateBanners}
      <Banner
        variant="info"
        message={"Pour rappel : le délai de suspension de signature (dit de standstill) de 11 jours ne concerne pas les marchés subséquents. Vous pouvez donc informer les candidats évincés à tout moment. N'oubliez pas de passer le marché subséquent à votre contrôle de légalité si vous y êtes soumis."}
      />

      <OperationMarchePreparation operation={operation} retenu={retenu} />

      <SectionPliable title="Signature et notification" defaultOpen>
        <div className="opms__marche-body">
          {canSign && <div className="opms__marche-sign-top no-print">{signerButton}</div>}

          {/* Prévisualisation du marché (pas pleine largeur) — disponible dès que le PDF existe. */}
          <div className="opms__marche-preview">
            <div className="opms__marche-pdf opms__marche-pdf--narrow">
              {loadingMsPdf ? (
                <p className="opms__marche-pdf-empty">Chargement du document PDF...</p>
              ) : msPdfBlobUrl ? (
                <iframe src={msPdfBlobUrl} title="Prévisualisation du Marché Subséquent" className="opms__marche-iframe" />
              ) : (
                <div className="opms__marche-pdf-empty">
                  <p>Le document PDF du marché subséquent n'est pas encore disponible.</p>
                  <p>Il sera généré lors de la validation de l'opération.</p>
                </div>
              )}
            </div>
          </div>

          {/* Statut 7 (mode automatique) : signature directe par le signataire designe */}
          {operationStatus === StatusId.ASigner && isUserDesignated && (
            <div className="opms__marche-signature">
              <h4>Signature du marché</h4>
              <p>Vous pouvez maintenant signer ce marché côté organisme.</p>
              <Button
                value={signing ? 'Signature en cours...' : 'Signer le marché'}
                onClick={signRetenu}
                disabled={signing}
              />
            </div>
          )}

          {/* Statut 13 "A signer" (mode manuscrite) : signataire + signature ou demande.
              La preview est desormais en haut de la section (toujours disponible). */}
          {operationStatus === StatusId.ANotifier && (
            <div className="opms__marche-signature">
              {isAcheteurSignataire(user?.role) && (
                <div className="opms__marche-signataire">
                  {/* Le nom du signataire est aussi re-saisi/valide dans la popup OTP ; « Modifier »
                      permet de l'ajuster en amont (prefill de SignatureFlow via initialNomComplet).
                      « Signer et notifier » est verrouille tant que l'edition du nom n'est pas validee. */}
                  <div className="opms__marche-signataire-line">
                    {editingSignataire ? (
                      <>
                        <input
                          className="opms__input opms__marche-signataire-input"
                          value={nomSignataireMs}
                          onChange={(e) => setNomSignataireMs(e.target.value)}
                          placeholder="Nom du signataire"
                        />
                        <button
                          type="button"
                          className="opms__candidats-link"
                          onClick={() => setEditingSignataire(false)}
                        >
                          Valider
                        </button>
                      </>
                    ) : (
                      <>
                        <span>
                          <strong>{nomSignataireMs || 'Le signataire désigné'}</strong> apparaîtra comme signataire.
                        </span>
                        <button
                          type="button"
                          className="opms__candidats-link"
                          onClick={() => setEditingSignataire(true)}
                        >
                          Modifier
                        </button>
                      </>
                    )}
                  </div>
                  <div className="opms__marche-signataire-action">{signerButton}</div>
                </div>
              )}

              {isAcheteurSimple(user?.role) && (
                <div className="opms__marche-request">
                  <h5>Demande de signature</h5>
                  <p>
                    Vous n'êtes pas autorisé à signer ce marché. Vous pouvez envoyer une demande de signature par
                    email à un signataire autorisé de votre organisme.
                  </p>
                  <div className="opms__marche-request-form">
                    <label htmlFor="opms-signataire-select">Envoyer la demande de signature à :</label>
                    {loadingSignataires ? (
                      <p>Chargement des signataires...</p>
                    ) : (
                      <select
                        id="opms-signataire-select"
                        className="opms__input"
                        value={selectedSignataireId ?? ''}
                        onChange={(e) => setSelectedSignataireId(e.target.value ? parseInt(e.target.value, 10) : null)}
                      >
                        <option value="">-- Sélectionner un signataire --</option>
                        {acheteurSignataires.map((sig) => (
                          <option key={sig.id} value={sig.id}>
                            {sig.prenom} {sig.nom} (
                            {sig.type_signataire === 'responsable_achat' ? 'Acheteur signataire admin' : 'Acheteur signataire'}
                            ){sig.email_contact ? ` - ${sig.email_contact}` : ''}
                          </option>
                        ))}
                      </select>
                    )}
                    <Button
                      variant="primary"
                      value={sendingRequest ? 'Envoi en cours...' : 'Envoyer la demande'}
                      onClick={handleRequestSignature}
                      disabled={sendingRequest || !selectedSignataireId}
                    />
                  </div>
                  {getDemandesSignature(operation.historique).length > 0 && (
                    <div className="opms__marche-request-history">
                      <h5>Historique des demandes de signature</h5>
                      {getDemandesSignature(operation.historique).map((entry, index) => (
                          <div key={index} className="opms__marche-request-entry">
                            <div className="opms__marche-request-label">{entry.label}</div>
                            <div className="opms__marche-request-date">
                              {entry.created_at &&
                                new Date(entry.created_at).toLocaleDateString('fr-FR', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Statut 9 (mode automatique) : acheteur en attente de la signature du prestataire */}
          {operationStatus === StatusId.EnAttenteSignatureAcheteur && (
            <div className="opms__marche-signature">
              <h4>En attente de la signature du prestataire</h4>
              <p>
                Le prestataire doit signer le marché avant que vous puissiez apposer votre signature électronique.
              </p>
              <p>
                Vous serez automatiquement notifié dès que le prestataire aura signé. Vous pourrez alors revenir ici
                pour compléter votre signature.
              </p>
            </div>
          )}

          {/* Statut 11 (signé) : telechargement du marche signe, EN BAS de la section — apres la
              previsualisation, donc dans l'ordre de lecture : on regarde le document, puis on le
              recupere. Il etait en haut, avant meme l'apercu. Il reste egalement dans la barre
              d'actions du bas de l'onglet (meme JSX, defini une fois). */}
          {marcheSigne && <div className="opms__marche-sign-bottom no-print">{downloadButton}</div>}

          {/* La notification retenu/non-retenus est desormais dans la section
              « Information aux candidats » ci-dessous (lignes + envoi + dates). */}
        </div>
      </SectionPliable>

      {/* Rappel des documents : section propre (Contrat + 2 CCTP + carte-bouton ouvrant le profil
          du titulaire pour le reste de ses documents et informations). */}
      <SectionPliable title="Rappel des documents associés à ce marché" defaultOpen>
        <div className="opms__marche-docs">
          <div className="opms__marche-docs-row">
            {marcheDocsLoading && marcheDocuments.length === 0 ? (
              <p className="opms__marche-pdf-empty">Chargement des documents...</p>
            ) : (
              <DocumentsList documents={marcheDocuments} variant="grid" />
            )}
            {retenu?.contrat?.entite?.id !== undefined && (
              <div
                className="document-card document-card-grid opms__marche-doc-more"
                role="button"
                tabIndex={0}
                onClick={openTitulaireProfil}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    openTitulaireProfil();
                  }
                }}
              >
                <div className="document-card-icon document-card-icon--grey">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" />
                  </svg>
                </div>
                <div className="document-card-body">
                  <span className="document-card-type">Voir les autres documents et informations du titulaire</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </SectionPliable>

      <OperationMarcheCandidats
        operation={operation}
        retenu={retenu}
        nonRetenus={nonRetenus}
        onSeeMessage={openPreview}
        onNotified={handleCandidatNotified}
        justNotified={justNotified}
      />

      {/* Rappel des bannieres d'etat juste avant les boutons (no-print : deja en tete de page). */}
      <div className="no-print">{stateBanners}</div>

      <div className="opms__actions no-print">
        {canAnnulerOperation(operationStatus) && (
          <Button
            value={isAnnuling ? 'Annulation...' : 'Annuler la consultation'}
            variant="cancel"
            onClick={() => setIsAnnulerOpen(true)}
            disabled={isAnnuling}
          />
        )}
        <PrintButton
          targetRef={printRef}
          value="Imprimer la page"
          fileName={`${operation.numero ?? ''} - Notifications et signatures`}
          suiviHeaderProps={{
            titre: operation.nom || '',
            sousTitre: '',
            subtitle: `${operation.lot_technique?.id ?? ''} - ${operation.lot_technique?.nom ?? ''}`,
            status: operation.status?.nom || 'Statut inconnu',
            statusColor: statusColorById(operation.status?.id),
            delais: operation.expire_at,
          }}
        />
        {canSign && signerButton}
        {marcheSigne && downloadButton}
        <NextTabButton
          label={notificationsCta.label}
          hidden={notificationsCta.hidden}
          onClick={() => navigate({ search: `?tab=${notificationsCta.target}` })}
        />
      </div>

      {/* Aperçu du mail de notification */}
      {isPreviewOpen && selectedCommandeForPreview && (
        <div className="opms__marche-modal-overlay" onClick={() => setIsPreviewOpen(false)}>
          <div className="opms__marche-modal" onClick={(e) => e.stopPropagation()}>
            <div className="opms__marche-modal-bar">
              {(() => {
                const cmd = selectedCommandeForPreview;
                const isRet = cmd.id === operation.id_commande;
                const dateEnvoi = computeDateEnvoi(cmd);
                return (
                  <span className="opms__marche-modal-title">
                    {dateEnvoi
                      ? `Envoyé le ${dateEnvoi}`
                      : `Aperçu du message de notification au ${isRet ? 'retenu' : 'non-retenu'}`}
                  </span>
                );
              })()}
              <div className="opms__marche-modal-actions">
                <Button value="PDF" variant="secondary" icon={<PdfIcon />} onClick={() => handlePrintMail(selectedCommandeForPreview)} />
                <Button value="Fermer" variant="primary" onClick={() => setIsPreviewOpen(false)} />
              </div>
            </div>
            <MailPreview selectedCommandeForPreview={selectedCommandeForPreview} generateMailPreview={generateMail} />
          </div>
        </div>
      )}

      {/* Modale d'annulation */}
      <ValidationPopUp
        isOpen={isAnnulerOpen}
        title="Annulation de la consultation"
        text="Êtes-vous sûr de vouloir annuler cette consultation ? L'opération et les commandes liées passeront en statut annulé. Cette action est irréversible."
        onClose={() => setIsAnnulerOpen(false)}
        onConfirm={handleAnnuler}
        cancelLabel="Non"
        confirmLabel="Oui, annuler"
        isLoading={isAnnuling}
      />

      {/* Signature manuscrite (OTP + dessin) — fix OTP : nom visible (hideNameEdit omis) + requireOtp explicite */}
      {showSignatureModal && retenu?.id !== undefined && (
        <SignatureFlow
          operationId={operation.id}
          commandeId={retenu.id}
          resourcePrefix="subsequents"
          requireOtp
          initialNomComplet={nomSignataireMs}
          onClose={() => setShowSignatureModal(false)}
          onSigned={() => {
            setShowSignatureModal(false);
            refetch();
          }}
        />
      )}

      {/* Succès signature automatique */}
      <ValidationPopUp
        isOpen={autoSignSuccess}
        title="Marché signé avec succès"
        text="Votre signature a été enregistrée. Un accusé de réception vous a été envoyé par email."
        onClose={() => {
          setAutoSignSuccess(false);
          refetch();
        }}
        onConfirm={() => {
          setAutoSignSuccess(false);
          refetch();
        }}
        confirmLabel="Fermer"
      />

      {/* Erreur d'action */}
      <ValidationPopUp
        isOpen={!!errorModal}
        title="Erreur"
        text={errorModal || ''}
        onClose={() => setErrorModal(null)}
        onConfirm={() => setErrorModal(null)}
        confirmLabel="Fermer"
      />

      {/* Popup profil du titulaire — ouverte sur l'onglet Documents (tab=1), via la carte-bouton. */}
      <ModalProfilMembre
        isOpen={showProfileModal}
        onClose={() => {
          setShowProfileModal(false);
          setProfile(null);
          setProfileError(null);
        }}
        profile={profile}
        isLoading={profileLoading}
        error={profileError}
        contactInfo={profileContact}
        initialTab={1}
        showCompletion={false}
        showPasserCommande={false}
      />
    </div>
  );
};

export default OperationMarche;
